import React from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { withStyleProps } from "Utils/styleProps";

import { Popover } from "Cobalt/Popover";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import SIOverlay from "Common/SIOverlay";

import type { StyleProps } from "Utils/styleProps";

import makeBem from "Utils/makeBem";

type TooltipProps = {
  id?: string;
  placement?: "right" | "left" | "top" | "bottom";
  content: React.ReactNode;
};
type BadgeProps = StyleProps & {
  color: string;
  className?: string;
  children: React.ReactNode;
  icon?: string;
  iconFlavor?: "solid" | "regular" | "light" | "brands";
  tooltip?: TooltipProps;
};
const bem = makeBem("CobaltBadge");

const UnstyledBadge = ({ color: backgroundColor, className, children, icon, iconFlavor = "solid", tooltip }: BadgeProps) => {
  const BadgeElement = (
    <Div
      className={classnames(bem(), className)}
      style={{
        backgroundColor,
      }}
    >
      {icon && <Icon className={classnames(bem("Icon"))} icon={icon} flavor={iconFlavor} mr={2} />}
      {children}
    </Div>
  );

  if (tooltip) {
    const { id: tooltipId = "badge_tooltip", placement = "top", content: tooltipContent, ...popoverProps } = tooltip;
    return (
      <SIOverlay
        placement={placement}
        overlayContent={
          <Popover showBackdrop={false} id={tooltipId} {...popoverProps}>
            {tooltipContent}
          </Popover>
        }
        overlayTarget={BadgeElement}
      />
    );
  }

  return BadgeElement;
};

UnstyledBadge.propTypes = {
  color: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  icon: PropTypes.string,
  iconFlavor: PropTypes.oneOf(["solid", "regular", "light", "brands"]),
};

/**
 * NEW in Cobalt: Badge component
 * A badge is a small piece of information to highlight
 * It may optionally show a tooltip on hover w/ details
 */
export const Badge = withStyleProps(UnstyledBadge);
